import { ROUTES } from "@/constants/routes";

/**
 * Query param the voice pages read to begin recording immediately on arrival.
 * Set by NativeQuickActionListener when a Home Screen quick action or the
 * "New Voice Note" App Shortcut (Action Button / Siri) launched the app.
 */
export const VOICE_AUTOSTART_PARAM = "autostart";

/** Where the user currently is, for carrying context onto the target route. */
export interface CurrentLocation {
  pathname: string;
  search: string;
}

const NO_LOCATION: CurrentLocation = { pathname: "", search: "" };

// Built from the route constant so a route change breaks this visibly.
const WORKSPACE_DETAIL_PATTERN = new RegExp(
  `^${ROUTES.WORKSPACES.DETAIL.replace(":workspaceId", "([^/]+)")}/?$`,
);

/**
 * The workspace the user is currently scoped to, if any: an explicit
 * `?workspaceId=` (the mobile voice/home contract) or the
 * `/workspaces/:workspaceId` detail route — which is where the
 * workspace-scoped mobile home actually lives, as a path param.
 */
function workspaceScopeFrom(current: CurrentLocation): string | null {
  const fromSearch = new URLSearchParams(current.search).get("workspaceId");
  if (fromSearch) return fromSearch;
  return WORKSPACE_DETAIL_PATTERN.exec(current.pathname)?.[1] ?? null;
}

/**
 * Maps a quick-action deep link to its in-app route.
 *
 * The native side (QuickActions.swift) synthesizes
 * `<bundle-id>://quick-action/<action>?ts=<epoch-ms>` and pushes it through
 * Capacitor's ApplicationDelegateProxy, so we match on the host — the scheme
 * varies between the dev and prod bundle ids.
 *
 * A workspace scope at the current location (see workspaceScopeFrom) is
 * carried onto the voice route, so a quick action fired from a
 * workspace-scoped screen — or over an in-progress scoped recording — keeps
 * its scope (recording target, send destination, and back navigation)
 * instead of silently rescoping to the private workspace.
 */
export function quickActionRoute(
  url: string | undefined,
  current: CurrentLocation = NO_LOCATION,
): string | null {
  if (!url || !url.includes("://quick-action/")) return null;
  const action = url
    .split("://quick-action/")[1]
    ?.split(/[?#]/)[0]
    ?.replace(/\/+$/, "");
  if (action === "voice-note") {
    const params = new URLSearchParams();
    params.set(VOICE_AUTOSTART_PARAM, "1");
    const workspaceId = workspaceScopeFrom(current);
    if (workspaceId) params.set("workspaceId", workspaceId);
    return `${ROUTES.MOBILE.VOICE}?${params.toString()}`;
  }
  return null;
}

/**
 * Consumption ledger. Capacitor's `App.getLaunchUrl()` keeps returning the
 * native-side `lastURL` for the entire APP process — which outlives this JS
 * process whenever the webview reloads (WKWebView content-process death,
 * dev reload). A module latch alone would reset with the JS process and
 * replay the stale URL into a recording the user never asked for, so the
 * last consumed URL is also persisted; the native `ts` param makes every
 * delivery unique, so an exact match can only ever be a replay while a
 * genuine new launch always differs.
 */
const CONSUMED_STORAGE_KEY = "cohesive.quickAction.lastConsumed";

/** Launch URLs older than this are stale (backstop if storage was cleared). */
const LAUNCH_URL_MAX_AGE_MS = 5 * 60 * 1000;

let launchUrlSeen = false;

function readConsumed(): string | null {
  try {
    return window.localStorage.getItem(CONSUMED_STORAGE_KEY);
  } catch {
    return null;
  }
}

function markConsumed(url: string): void {
  try {
    window.localStorage.setItem(CONSUMED_STORAGE_KEY, url);
  } catch {
    // Storage unavailable — the in-process latch still covers this session.
  }
}

function deliveryTimestamp(url: string): number | null {
  const raw = /[?&]ts=(\d+)/.exec(url)?.[1];
  return raw ? Number(raw) : null;
}

/**
 * Cold-start entry point, for `App.getLaunchUrl()`. Returns the route at most
 * once per delivery: the module latch absorbs listener remounts (login
 * transitions swap the tree above it), the persisted ledger absorbs webview
 * reloads, and the age check absorbs a cleared ledger.
 */
export function takeLaunchQuickActionRoute(
  url: string | undefined,
  current: CurrentLocation = NO_LOCATION,
  now = Date.now(),
): string | null {
  if (launchUrlSeen) return null;
  const to = quickActionRoute(url, current);
  if (!to || !url) return null;
  // One verdict per JS process — the launch URL never changes within one.
  launchUrlSeen = true;
  if (url === readConsumed()) return null;
  const ts = deliveryTimestamp(url);
  if (ts !== null && now - ts > LAUNCH_URL_MAX_AGE_MS) return null;
  markConsumed(url);
  return to;
}

/**
 * Warm entry point, for live `appUrlOpen` events. Always fresh — but recorded
 * in the ledger, because the same URL becomes the app's `lastURL` and would
 * otherwise replay through the cold-start path after a webview reload.
 */
export function takeLiveQuickActionRoute(
  url: string | undefined,
  current: CurrentLocation = NO_LOCATION,
): string | null {
  const to = quickActionRoute(url, current);
  if (to && url) markConsumed(url);
  return to;
}

/** Test-only: reset the cold-start latch and ledger between cases. */
export function resetQuickActionStateForTest(): void {
  launchUrlSeen = false;
  try {
    window.localStorage.removeItem(CONSUMED_STORAGE_KEY);
  } catch {
    // ignore
  }
}
